import { Box } from '@factorialco/deprecated-design-system'
import { uniq } from 'lodash'

import { observer } from 'mobx-react'

import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import hasFeature from 'modules/core/lib/hasFeature'

import employees from 'modules/employees/store/collections/employees'
import PayrollPageLoading from 'modules/payroll_bundle/components/PayrollPageLoading'
import ResultStats from 'modules/payroll_bundle/components/results/ResultStats'
import ResultsTable from 'modules/payroll_bundle/components/results/ResultsTable'
import { useResultConceptItemsTable } from 'modules/payroll_bundle/hooks/useResultConceptItemsTable'
import {
  groupByConceptName,
  useResultConceptTotals,
} from 'modules/payroll_bundle/hooks/useResultConceptTotals'
import { useResultEmployees } from 'modules/payroll_bundle/hooks/useResultEmployees'
import { usePayrollResults } from 'modules/payroll_bundle/lib/PayrollResultContext'
import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import CurrencyWarning from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/_common/CurrencyWarning'

import ValidationsSidePanel from './ValidationsSidePanel'

interface Props {
  currency: string
}

const ResultContents = ({ currency }: Props) => {
  const { showResultValidationsSidePanel, resultDispatch } = usePayrollResults()
  const { currentPeriod, prevPeriod } = usePolicyPeriods()
  const { currentPeriodResultConceptTotals, otherPeriodResultConceptTotals } =
    useResultConceptTotals(currentPeriod?.id, prevPeriod?.id)
  const currentPolicyPeriodId = currentPeriod?.id
  const previousPolicyPeriodId = prevPeriod?.id

  const { loading: loadingResultEmployees, employeeIds: importedResultEmployeeIds } =
    useResultEmployees(currentPolicyPeriodId)

  const importedEmployees = employees.filter((employee) =>
    importedResultEmployeeIds.includes(employee.attributes.id)
  )
  const importedEmployeesLegalEntities = uniq(
    importedEmployees.map((employee) => employeeLegalEntity(employee))
  )

  // FIXME: Currency is undefined when there are no imported employees,
  // and has an 'any' because we are not using `legalEntity.currency` accessor
  // due to type mismatches. We should get this from the Legal Entities attached
  // to the policy, and try to fix the type of currency upstream.
  const currencies = uniq(
    importedEmployeesLegalEntities.map((legalEntity) => legalEntity.attributes.currency)
  ).sort()

  const { mappedResultConcepts } = useResultConceptItemsTable(
    currentPolicyPeriodId,
    previousPolicyPeriodId
  )

  const hasPayrollResultsValidation = hasFeature('dev_payroll_results_validation')

  function hideResultValidationsSidePanel() {
    resultDispatch({
      type: UPDATE_DATA,
      payload: {
        showResultValidationsSidePanel: false,
      },
    })
  }

  function handleOpenImportResultsModal() {
    resultDispatch({
      type: UPDATE_DATA,
      payload: {
        showUploadModal: true,
      },
    })
  }

  const currentPeriodResultConceptTotalsByConceptName = groupByConceptName(
    currentPeriodResultConceptTotals,
    mappedResultConcepts
  )
  const otherPeriodResultConceptTotalsByConceptName = groupByConceptName(
    otherPeriodResultConceptTotals,
    mappedResultConcepts
  )

  if (loadingResultEmployees) {
    return <PayrollPageLoading />
  }

  return (
    <Box gap="s24">
      {currencies.length > 1 && <CurrencyWarning currencies={currencies} currency={currency} />}
      <ResultStats
        currentPeriodResultConceptTotals={currentPeriodResultConceptTotals}
        currentPeriodResultConceptTotalsByConceptName={
          currentPeriodResultConceptTotalsByConceptName
        }
        otherPeriodResultConceptTotalsByConceptName={otherPeriodResultConceptTotalsByConceptName}
        currency={currency}
        importedEmployeeIds={importedResultEmployeeIds}
        onOpenImportResultsModal={handleOpenImportResultsModal}
        mappedResultConcepts={mappedResultConcepts}
      />
      <ResultsTable currency={currency} />
      {hasPayrollResultsValidation && (
        <ValidationsSidePanel
          open={showResultValidationsSidePanel}
          onClose={() => hideResultValidationsSidePanel()}
          currency={currency}
          employeeIds={uniq(importedResultEmployeeIds)}
        />
      )}
    </Box>
  )
}

export default observer(ResultContents)
